import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import {
  getEscalationRules, getEscalationBreaches,
  createEscalationRule, updateEscalationRule, deleteEscalationRule
} from '../services/api';
import Swal from 'sweetalert2';
import { Siren, Plus, Pencil, Trash2, Check, X, ToggleLeft, ToggleRight, AlertTriangle, User, Mail, Clock, RefreshCw, ExternalLink } from 'lucide-react';

const PRIORITIES   = ['critical', 'high', 'medium', 'low', 'all'];
const BREACH_TYPES = [{ value: 'response', label: 'First Response' }, { value: 'resolution', label: 'Resolution' }];
const ACTIONS      = [{ value: 'notify', label: 'Notify only' }, { value: 'escalate', label: 'Escalate ticket' }];

const emptyForm = { priority: 'critical', breach_type: 'response', threshold_minutes: 30, action: 'notify', notify_roles: ['admin'] };

const PRIORITY_COLOR = { critical: '#dc2626', high: '#ea580c', medium: '#ca8a04', low: '#16a34a', all: '#7c3aed' };

function fmtOverdue(mins) {
  if (!mins || mins < 1) return '< 1m overdue';
  if (mins < 60) return `${mins}m overdue`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m > 0 ? `${h}h ${m}m overdue` : `${h}h overdue`;
}

export default function EscalationRulesPage() {
  const navigate = useNavigate();
  const [rules,    setRules]    = useState([]);
  const [breaches, setBreaches] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [bLoading, setBLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(emptyForm);
  const [saving,   setSaving]   = useState(false);

  const fetchRules = async () => {
    setLoading(true);
    try { const r = await getEscalationRules(); setRules(r.data); } catch (_) {}
    setLoading(false);
  };

  const fetchBreaches = async () => {
    setBLoading(true);
    try { const r = await getEscalationBreaches(); setBreaches(r.data || []); } catch (_) {}
    setBLoading(false);
  };

  const fetch = () => { fetchRules(); fetchBreaches(); };

  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit   = (r) => {
    setEditing(r.id);
    setForm({
      priority: r.priority,
      breach_type: r.breach_type,
      threshold_minutes: r.threshold_minutes,
      action: r.action,
      notify_roles: r.notify_roles || ['admin'],
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await updateEscalationRule(editing, form);
      } else {
        await createEscalationRule(form);
      }
      setShowForm(false);
      fetch();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Could not save rule.', 'error');
    }
    setSaving(false);
  };

  const handleToggle = async (rule) => {
    try {
      await updateEscalationRule(rule.id, { is_active: !rule.is_active });
      fetch();
    } catch (_) {}
  };

  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: 'Delete Rule?', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#dc2626',
    });
    if (!res.isConfirmed) return;
    try { await deleteEscalationRule(id); fetch(); } catch (_) {}
  };

  const toggleRole = (role) => {
    setForm(f => ({
      ...f,
      notify_roles: f.notify_roles.includes(role)
        ? f.notify_roles.filter(r => r !== role)
        : [...f.notify_roles, role],
    }));
  };

  return (
    <AppLayout pageTitle="Escalation Rules">
      <style>{spinStyle}</style>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Siren size={22} color="var(--primary)" />
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-main)' }}>Escalation Rules</h2>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
                Auto-alerts run every 5 minutes and notify or escalate breached tickets.
              </p>
            </div>
          </div>
          <button onClick={openCreate} style={btnPrimary}>
            <Plus size={14} /> New Rule
          </button>
        </div>

        {/* Rule Form */}
        {showForm && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 700, color: 'var(--text-main)' }}>
              {editing ? 'Edit Rule' : 'New Escalation Rule'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={label}>Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} style={select}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p === 'all' ? 'All priorities' : p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={label}>Breach Type</label>
                  <select value={form.breach_type} onChange={e => setForm(f => ({...f, breach_type: e.target.value}))} style={select}>
                    {BREACH_TYPES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={label}>Threshold (minutes)</label>
                  <input
                    type="number" min={1} value={form.threshold_minutes}
                    onChange={e => setForm(f => ({...f, threshold_minutes: parseInt(e.target.value) || 1}))}
                    style={input}
                  />
                </div>
                <div>
                  <label style={label}>Action</label>
                  <select value={form.action} onChange={e => setForm(f => ({...f, action: e.target.value}))} style={select}>
                    {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={label}>Notify Roles</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {['admin', 'consultant'].map(role => (
                    <label key={role} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: 'var(--text-sub)' }}>
                      <input
                        type="checkbox"
                        checked={form.notify_roles.includes(role)}
                        onChange={() => toggleRole(role)}
                        style={{ width: 14, height: 14 }}
                      />
                      {role.charAt(0).toUpperCase()+role.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={saving} style={btnPrimary}>
                  <Check size={14} /> {saving ? 'Saving…' : 'Save Rule'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} style={btnSecondary}>
                  <X size={14} /> Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Rules Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>
        ) : rules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
            <Siren size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>No escalation rules configured yet.</p>
          </div>
        ) : (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-body)' }}>
                  {['Priority','Breach Type','Threshold','Action','Notify','Status',''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rules.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border-light)', opacity: r.is_active ? 1 : 0.5 }}>
                    <td style={td}>
                      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 10, background: `${PRIORITY_COLOR[r.priority] || '#6b7280'}1a`, color: PRIORITY_COLOR[r.priority] || '#6b7280', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>
                        {r.priority}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: 13, color: 'var(--text-sub)' }}>
                        {r.breach_type === 'response' ? 'First Response' : 'Resolution'}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: 13, color: 'var(--text-main)', fontWeight: 600 }}>
                        {r.threshold_minutes >= 60
                          ? `${(r.threshold_minutes/60).toFixed(r.threshold_minutes%60===0?0:1)}h`
                          : `${r.threshold_minutes}m`}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                        background: r.action === 'escalate' ? '#fee2e2' : '#eff6ff',
                        color: r.action === 'escalate' ? '#dc2626' : '#2563eb',
                      }}>
                        {r.action === 'escalate' ? 'Escalate' : 'Notify'}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {(r.notify_roles || []).join(', ')}
                      </span>
                    </td>
                    <td style={td}>
                      <button onClick={() => handleToggle(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, color: r.is_active ? '#16a34a' : 'var(--text-muted)', fontSize: 12, fontWeight: 600 }}>
                        {r.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                        {r.is_active ? 'Active' : 'Off'}
                      </button>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button onClick={() => openEdit(r)} style={iconBtn} title="Edit">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => handleDelete(r.id)} style={{ ...iconBtn, color: '#dc2626' }} title="Delete">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Live Breach Monitor ── */}
        <div style={{ marginTop: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={17} color="#dc2626" />
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-main)' }}>Live Breach Monitor</span>
              {breaches.length > 0 && (
                <span style={{ background: '#dc2626', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                  {breaches.length} active
                </span>
              )}
            </div>
            <button onClick={fetchBreaches} disabled={bLoading} title="Refresh" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>
              <RefreshCw size={13} style={{ animation: bLoading ? 'spin 0.8s linear infinite' : 'none' }} /> Refresh
            </button>
          </div>

          {bLoading ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 13 }}>Loading breaches…</div>
          ) : breaches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 28, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
              <span style={{ fontSize: 22 }}>✅</span>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#15803d', fontWeight: 600 }}>No SLA breaches right now. All tickets are on track.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {breaches.map((b, i) => (
                <div key={`${b.ticket_id}-${b.breach_type}-${i}`} style={{
                  background: 'var(--bg-card)', border: `1px solid ${b.action === 'escalate' ? '#fecaca' : '#fed7aa'}`,
                  borderLeft: `4px solid ${PRIORITY_COLOR[b.priority] || '#dc2626'}`,
                  borderRadius: 10, padding: '14px 16px',
                  display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center',
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {/* Row 1: ticket number + title + overdue badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.3px' }}>{b.ticket_number}</span>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-main)' }}>{b.title}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: `${PRIORITY_COLOR[b.priority]}1a`, color: PRIORITY_COLOR[b.priority], textTransform: 'uppercase' }}>
                        {b.priority}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: '#fef3c7', color: '#92400e', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Clock size={10} /> {fmtOverdue(b.minutes_overdue)}
                      </span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: b.breach_type === 'response' ? '#eff6ff' : '#fef2f2', color: b.breach_type === 'response' ? '#2563eb' : '#dc2626', fontWeight: 600 }}>
                        {b.breach_type === 'response' ? 'No First Response' : 'Resolution SLA Passed'}
                      </span>
                    </div>
                    {/* Row 2: consultant info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <User size={13} color={b.assigned_name ? 'var(--primary)' : '#dc2626'} />
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: b.assigned_name ? 'var(--text-sub)' : '#dc2626' }}>
                          {b.assigned_name || '⚠ Not assigned to anyone'}
                        </span>
                      </div>
                      {b.assigned_email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <Mail size={12} color="var(--text-muted)" />
                          <a href={`mailto:${b.assigned_email}`} style={{ fontSize: 12, color: 'var(--primary)', textDecoration: 'none' }}>
                            {b.assigned_email}
                          </a>
                        </div>
                      )}
                      {b.action === 'escalate' && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: '#fee2e2', color: '#dc2626' }}>
                          AUTO-ESCALATED
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Right side: open ticket button */}
                  <button
                    onClick={() => navigate(`/tickets/${b.ticket_id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-body)', color: 'var(--text-sub)', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    <ExternalLink size={12} /> Open Ticket
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info box */}
        <div style={{ marginTop: 20, padding: '14px 18px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12.5, color: 'var(--text-muted)' }}>
          <strong style={{ color: 'var(--text-sub)' }}>How it works:</strong> The engine checks all open tickets every 5 minutes.
          When a ticket's first response or resolution time exceeds the threshold, it creates an in-app notification and sends an email.
          The same ticket is re-alerted at most once per hour to prevent spam.
        </div>
      </div>
    </AppLayout>
  );
}

const spinStyle = `@keyframes spin { to { transform: rotate(360deg); } }`;

const label = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.4px' };
const select = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-body)', color: 'var(--text-main)', fontSize: 13 };
const input  = { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-body)', color: 'var(--text-main)', fontSize: 13, boxSizing: 'border-box' };
const td     = { padding: '11px 14px', fontSize: 13, verticalAlign: 'middle' };
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 5, borderRadius: 6, display: 'flex', alignItems: 'center' };
const btnPrimary   = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-sub)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' };
